import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Form,
  Label,
  Input,
  FormGroup,
  Alert,
} from 'reactstrap';
import notify, { MESSAGES } from '../../../../utils/notify';
import { format } from 'date-fns';
import useHpsRequest from '../../hooks/useHpsRequest';
import SubmitConfirmModal from '../SubmitConfirmModal';
import { formatDateToDDMMYYYY } from '../../../../utils/dateformatter';
import {
  displayValue,
  formatProposalDeadline,
  getContractPeriodProps,
  getContractTypeLabel,
  getProcurementTypeLabel,
  formatProcurementProductTypeNames,
} from '../../utils/tenderOpeningHelpers';
import {
  hasMultipleProductTypes,
  requiresLpgCargoRatioForCargo,
} from '../../../../shared/offer-domain/constants';
import HpsParcelSection from './HpsParcel';
import Can from '../../../../Components/Common/Can';
import { buttonProps } from '../../../../utils/a11y';
import ActionButton from '../../../../Components/Common/ActionButton';
import { SkeletonCard } from '../../../../Components/Common/Skeleton';

const FIELD_ORDER = [
  'titleNotaDinas',
  'noNotaDinas',
  'hpsRequestDate',
  'documentNotaDinas',
];

const InfoField = ({ label, value, error }) => (
  <Col md={6} lg={4}>
    <div className='border-start border-2 border-primary ps-3'>
      <Label className='fw-semibold small text-dark mb-1'>{label}</Label>
      <p className='mb-0 text-dark fw-medium'>{displayValue(value)}</p>
      {error && <div className='text-danger small mt-1'>{error}</div>}
    </div>
  </Col>
);

const toDateInputValue = value => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return format(date, 'yyyy-MM-dd');
};

const RequestHpsMarket = ({
  procId,
  procurement,
  isLoadingProcurement,
  onRefreshProcurement,
  parcels = [],
  isLoadingParcels = false,
}) => {
  const {
    uploadDocument,
    saveAsDraftRequestHps,
    sendRequestHps,
    exportParcelToExcel,
    isExportingExcel,
  } = useHpsRequest({ procId, procurement, onRefreshProcurement });

  const [form, setForm] = useState({
    titleNotaDinas: '',
    noNotaDinas: '',
    hpsRequestDate: '',
    documentNotaDinas: '',
  });
  const [errors, setErrors] = useState({});
  const [savingAction, setSavingAction] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileAttachment, setFileAttachment] = useState(null);
  const [cargoRatioStatusByParcel, setCargoRatioStatusByParcel] = useState({});
  const [cargoRatioErrorParcelId, setCargoRatioErrorParcelId] = useState(null);

  // Cargo ratio is required per cargo, by the product type of that cargo
  const requiresCargoRatio = parcels.some(parcel =>
    requiresLpgCargoRatioForCargo(procurement, parcel)
  );
  const parcelIdsKey = parcels.map(parcel => parcel.id).join(',');

  const handleCargoRatioStatusChange = useCallback((parcelId, isValid) => {
    if (!parcelId) return;
    setCargoRatioStatusByParcel(prev => {
      if (prev[parcelId] === isValid) return prev;
      return { ...prev, [parcelId]: isValid };
    });
  }, []);

  const handleClearCargoRatioError = useCallback(parcelId => {
    setCargoRatioErrorParcelId(prev => (prev === parcelId ? null : prev));
  }, []);

  useEffect(() => {
    setCargoRatioStatusByParcel({});
    setCargoRatioErrorParcelId(null);
  }, [procurement?.id, parcelIdsKey]);

  useEffect(() => {
    if (
      cargoRatioErrorParcelId &&
      cargoRatioStatusByParcel[cargoRatioErrorParcelId] === true
    ) {
      setCargoRatioErrorParcelId(null);
    }
  }, [cargoRatioErrorParcelId, cargoRatioStatusByParcel]);

  const notaDinasSectionRef = useRef(null);
  const fileInputRef = useRef(null);
  const focusTimeoutRef = useRef(null);
  const fieldRefs = {
    titleNotaDinas: useRef(null),
    noNotaDinas: useRef(null),
    hpsRequestDate: useRef(null),
    documentNotaDinas: fileInputRef,
  };

  const isHpsSubmitted = procurement?.isRequestHpsCompleted === true;

  useEffect(
    () => () => {
      if (focusTimeoutRef.current) {
        window.clearTimeout(focusTimeoutRef.current);
      }
    },
    []
  );

  const clearPendingFocus = () => {
    if (focusTimeoutRef.current) {
      window.clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
  };

  const scheduleFocus = (callback, delay = 50) => {
    clearPendingFocus();
    focusTimeoutRef.current = window.setTimeout(() => {
      focusTimeoutRef.current = null;
      callback();
    }, delay);
  };

  useEffect(() => {
    if (!procurement) return;

    setForm({
      titleNotaDinas: procurement.titleNotaDinas || '',
      noNotaDinas: procurement.noNotaDinas || '',
      hpsRequestDate: toDateInputValue(
        procurement.dateNotaDinas || procurement.hpsRequestDate
      ),
    });
    setErrors({});
  }, [procurement]);

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleFileChange = (e, _fieldName) => {
    const file = e.target.files[0];
    if (file) {
      setFileAttachment(file);
      setErrors(prev => ({ ...prev, documentNotaDinas: undefined }));
    }
  };

  const scrollToFirstError = nextErrors => {
    const firstField = FIELD_ORDER.find(key => nextErrors[key]);
    if (!firstField) return;

    clearPendingFocus();
    setCargoRatioErrorParcelId(null);

    notaDinasSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    scheduleFocus(() => {
      const target = fieldRefs[firstField]?.current;
      target?.focus?.({ preventScroll: true });
      target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }, 250);
  };

  const validateForm = ({ isSubmit }) => {
    const nextErrors = {};

    if (isSubmit) {
      if (!form.titleNotaDinas?.trim()) {
        nextErrors.titleNotaDinas = 'Title Nota Dinas is required';
      }
      if (!form.noNotaDinas?.trim()) {
        nextErrors.noNotaDinas = 'No. Nota Dinas is required';
      }
      if (!form.hpsRequestDate) {
        nextErrors.hpsRequestDate = 'Date Nota Dinas is required';
      }
      // Validasi dokumen: boleh salah satu dari (a) ada dokumen tersimpan di server,
      // atau (b) user baru upload file baru di sesi ini.
      if (
        !fileAttachment &&
        !uploadDocument.items?.length &&
        !procurement?.documentNotaDinas &&
        !procurement?.documentNotaDinasUrl
      ) {
        nextErrors.documentNotaDinas = 'At least one document is required';
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      scrollToFirstError(nextErrors);
      notify.warning(MESSAGES.mandatory);
      return false;
    }

    return true;
  };

  const validateCargoRatioSelection = () => {
    if (!requiresCargoRatio) return true;

    const invalidParcel = parcels.find(
      parcel =>
        requiresLpgCargoRatioForCargo(procurement, parcel) &&
        cargoRatioStatusByParcel[parcel.id] !== true
    );

    if (invalidParcel) {
      clearPendingFocus();
      // Pastikan highlight/focus form tidak aktif bersamaan dengan cargo.
      setErrors({});
      setCargoRatioErrorParcelId(invalidParcel.id);
      notify.warning(
        'Please select at least one cargo ratio for each parcel, then click Select Cargo Ratio.'
      );

      scheduleFocus(() => {
        const activeElement = document.activeElement;
        if (
          activeElement &&
          typeof activeElement.blur === 'function' &&
          (activeElement.tagName === 'INPUT' ||
            activeElement.tagName === 'TEXTAREA' ||
            activeElement.tagName === 'SELECT')
        ) {
          activeElement.blur();
        }

        const target = document.getElementById(
          `cargo-ratio-selection-${invalidParcel.id}`
        );
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target?.focus?.({ preventScroll: true });
      }, 50);

      return false;
    }

    setCargoRatioErrorParcelId(null);
    return true;
  };

  const handleSaveDraft = async () => {
    if (!validateCargoRatioSelection()) return;

    setSavingAction('draft');
    try {
      await saveAsDraftRequestHps(form, fileAttachment);
      notify.success('Draft saved successfully.');
      setFileAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onRefreshProcurement?.();
    } catch (err) {
      notify.apiError(err, 'save draft');
    } finally {
      setSavingAction(null);
    }
  };

  const handleSendRequestClick = () => {
    if (!validateForm({ isSubmit: true })) return;
    if (!validateCargoRatioSelection()) return;
    setShowConfirmModal(true);
  };

  const handleSendRequest = async () => {
    if (!validateForm({ isSubmit: true })) return;
    if (!validateCargoRatioSelection()) return;

    setSavingAction('send');
    try {
      await sendRequestHps(form, fileAttachment);
      notify.success('Request HPS sent successfully.');
      setFileAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onRefreshProcurement?.();
    } catch (err) {
      notify.apiError(err, 'send request HPS');
    } finally {
      setSavingAction(null);
    }
  };

  const openDocument = url => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (isLoadingProcurement && !procurement) {
    return (
      <>
        <SkeletonCard lines={4} className='mb-3' />
        <SkeletonCard lines={6} className='mb-3' />
        <SkeletonCard lines={4} />
      </>
    );
  }

  const periodProps = getContractPeriodProps(procurement);

  return (
    <>
      <Row>
        <Col xs={12}>
          <Card className='border shadow-sm'>
            <CardBody>
              <div className='d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3'>
                <div>
                  <h5 className='fw-semibold mb-1'>Request HPS</h5>
                  <p className='text-muted small mb-0'>
                    Submit HPS Market request information for this procurement.
                  </p>
                </div>
              </div>

              <Row className='g-3'>
                <InfoField
                  label='HPS Request Number'
                  value={procurement?.hpsRequestNumber || '-'}
                />
                <InfoField
                  label='Request Date'
                  value={
                    formatDateToDDMMYYYY(procurement?.hpsRequestDate) || '-'
                  }
                />
              </Row>
              <hr className='my-4 border-secondary' />

              <div
                ref={notaDinasSectionRef}
                style={{ scrollMarginTop: 96, scrollMarginBottom: 120 }}
              >
                <Form>
                  <Row className='g-3'>
                    {isHpsSubmitted ? (
                      <>
                        <InfoField
                          label='Title Nota Dinas'
                          value={form.titleNotaDinas}
                          error={errors.titleNotaDinas}
                        />
                        <InfoField
                          label='No. Nota Dinas'
                          value={form.noNotaDinas}
                          error={errors.noNotaDinas}
                        />
                        <InfoField
                          label='Date Nota Dinas'
                          value={
                            form.hpsRequestDate
                              ? formatDateToDDMMYYYY(form.hpsRequestDate)
                              : '-'
                          }
                          error={errors.hpsRequestDate}
                        />
                        <Col md={12} lg={12}>
                          <div className='mb-3'>
                            <Label className='fw-semibold small text-dark mb-2'>
                              Document Nota Dinas
                            </Label>

                            <div className='border rounded-3 p-2 d-flex align-items-center justify-content-between bg-light'>
                              <div className='d-flex align-items-center overflow-hidden me-2'>
                                <div
                                  className='d-flex align-items-center justify-content-center rounded bg-primary-subtle p-2 me-2 text-primary'
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <i className='ri-file-text-line fs-20'></i>
                                </div>

                                <span className='small text-dark fw-medium'>
                                  {(() => {
                                    const rawName =
                                      procurement?.documentNotaDinasName ||
                                      procurement?.documentNotaDinas;
                                    if (!rawName) return 'Nota Dinas Document';
                                    return rawName.includes('_')
                                      ? rawName.substring(
                                          rawName.indexOf('_') + 1
                                        )
                                      : rawName;
                                  })()}
                                </span>
                              </div>

                              {procurement?.documentNotaDinasUrl && (
                                <ActionButton
                                  variant='secondary'
                                  size='sm'
                                  icon='ri-eye-line'
                                  onClick={() =>
                                    openDocument(
                                      procurement.documentNotaDinasUrl
                                    )
                                  }
                                >
                                  View
                                </ActionButton>
                              )}
                            </div>
                          </div>
                        </Col>
                        {procurement?.documentNotaDinasRilisUrl && (
                          <Col md={12} lg={12}>
                            <div className='mb-3'>
                              <Label className='fw-semibold small text-dark mb-2'>
                                Document Nota Dinas Rilis
                              </Label>
                              <div className='border rounded-3 p-2 d-flex align-items-center justify-content-between bg-light'>
                                <div className='d-flex align-items-center overflow-hidden me-2'>
                                  <div
                                    className='d-flex align-items-center justify-content-center rounded bg-primary-subtle p-2 me-2 text-primary'
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <i className='ri-file-text-line fs-20'></i>
                                  </div>
                                  <span className='small text-dark fw-medium'>
                                    {(() => {
                                      const rawName =
                                        procurement.documentNotaDinasRilis ||
                                        'Nota Dinas Rilis Document';
                                      return rawName.includes('_')
                                        ? rawName.substring(
                                            rawName.indexOf('_') + 1
                                          )
                                        : rawName;
                                    })()}
                                  </span>
                                </div>
                                <ActionButton
                                  variant='secondary'
                                  size='sm'
                                  icon='ri-eye-line'
                                  onClick={() =>
                                    openDocument(
                                      procurement.documentNotaDinasRilisUrl
                                    )
                                  }
                                >
                                  View
                                </ActionButton>
                              </div>
                            </div>
                          </Col>
                        )}
                      </>
                    ) : (
                      <>
                        <Alert
                          color='warning'
                          className='d-flex align-items-start gap-2'
                        >
                          <i className='ri-error-warning-line flex-shrink-0 mt-1'></i>
                          <span className='small'>
                            Please submit the HPS Market request through
                            P-Office first before you can provide the
                            information below
                          </span>
                        </Alert>
                        <Col md={6} lg={4}>
                          <FormGroup>
                            <Label className='fw-semibold small text-dark'>
                              Title Nota Dinas
                            </Label>
                            <Input
                              innerRef={fieldRefs.titleNotaDinas}
                              name='titleNotaDinas'
                              type='text'
                              placeholder='Input title nota dinas'
                              className='bg-white'
                              value={form.titleNotaDinas}
                              onChange={handleFormChange}
                              invalid={!!errors.titleNotaDinas}
                              aria-invalid={!!errors.titleNotaDinas}
                              aria-describedby={
                                errors.titleNotaDinas
                                  ? 'titleNotaDinas-error'
                                  : undefined
                              }
                            />
                            {errors.titleNotaDinas && (
                              <div
                                className='text-danger small mt-1'
                                id='titleNotaDinas-error'
                                role='alert'
                              >
                                {errors.titleNotaDinas}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md={6} lg={4}>
                          <FormGroup>
                            <Label className='fw-semibold small text-dark'>
                              No. Nota Dinas
                            </Label>
                            <Input
                              innerRef={fieldRefs.noNotaDinas}
                              name='noNotaDinas'
                              type='text'
                              placeholder='Input no. nota dinas'
                              className='bg-white'
                              value={form.noNotaDinas}
                              onChange={handleFormChange}
                              invalid={!!errors.noNotaDinas}
                              aria-invalid={!!errors.noNotaDinas}
                              aria-describedby={
                                errors.noNotaDinas
                                  ? 'noNotaDinas-error'
                                  : undefined
                              }
                            />
                            {errors.noNotaDinas && (
                              <div
                                className='text-danger small mt-1'
                                id='noNotaDinas-error'
                                role='alert'
                              >
                                {errors.noNotaDinas}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md={6} lg={4}>
                          <FormGroup>
                            <Label className='fw-semibold small text-dark'>
                              Date Nota Dinas
                            </Label>
                            <Input
                              innerRef={fieldRefs.hpsRequestDate}
                              name='hpsRequestDate'
                              type='date'
                              className='bg-white'
                              value={form.hpsRequestDate}
                              onChange={handleFormChange}
                              invalid={!!errors.hpsRequestDate}
                              aria-invalid={!!errors.hpsRequestDate}
                              aria-describedby={
                                errors.hpsRequestDate
                                  ? 'hpsRequestDate-error'
                                  : undefined
                              }
                            />
                            {errors.hpsRequestDate && (
                              <div
                                className='text-danger small mt-1'
                                id='hpsRequestDate-error'
                                role='alert'
                              >
                                {errors.hpsRequestDate}
                              </div>
                            )}
                          </FormGroup>
                        </Col>

                        <Col md={12} lg={12}>
                          <FormGroup>
                            <Label className='fw-semibold small text-dark mb-2'>
                              Document Nota Dinas
                            </Label>

                            <input
                              type='file'
                              id='fileAttachment'
                              name='fileAttachment'
                              onChange={handleFileChange}
                              disabled={!!savingAction}
                              ref={fileInputRef}
                              style={{ display: 'none' }}
                            />

                            {fileAttachment ? (
                              <div className='border rounded-3 p-2 d-flex align-items-center justify-content-between bg-light border-dashed'>
                                <div className='d-flex align-items-center overflow-hidden me-2'>
                                  <div
                                    className='d-flex align-items-center justify-content-center rounded bg-primary-subtle p-2 me-2 text-primary'
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <i className='ri-file-text-line fs-20'></i>
                                  </div>
                                  <span className='small text-dark text-truncate fw-medium'>
                                    {fileAttachment.name}
                                  </span>
                                </div>
                                <ActionButton
                                  variant='icon'
                                  size='sm'
                                  danger
                                  icon='ri-delete-bin-line'
                                  label='Remove file'
                                  confirm={{
                                    title: 'Remove file?',
                                    message:
                                      'The selected file will be removed from this form.',
                                    confirmLabel: 'Remove file',
                                  }}
                                  disabled={!!savingAction}
                                  onClick={() => {
                                    setFileAttachment(null);
                                    if (fileInputRef.current) {
                                      fileInputRef.current.value = '';
                                    }
                                  }}
                                />
                              </div>
                            ) : procurement?.documentNotaDinasName ||
                              procurement?.documentNotaDinas ||
                              procurement?.documentNotaDinasUrl ? (
                              <div className='border rounded-3 p-2 d-flex align-items-center justify-content-between bg-light'>
                                <div className='d-flex align-items-center overflow-hidden me-2'>
                                  <div
                                    className='d-flex align-items-center justify-content-center rounded bg-primary-subtle p-2 me-2 text-primary'
                                    style={{
                                      width: '36px',
                                      height: '36px',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <i className='ri-file-text-line fs-20'></i>
                                  </div>
                                  <span className='small text-dark text-truncate fw-medium'>
                                    {(() => {
                                      const rawName =
                                        procurement?.documentNotaDinasName ||
                                        procurement?.documentNotaDinas;
                                      if (!rawName)
                                        return 'Nota Dinas Document';
                                      return rawName.includes('_')
                                        ? rawName.substring(
                                            rawName.indexOf('_') + 1
                                          )
                                        : rawName;
                                    })()}
                                  </span>
                                </div>
                                <div className='d-flex align-items-center gap-1 flex-shrink-0'>
                                  {procurement?.documentNotaDinasUrl && (
                                    <ActionButton
                                      variant='secondary'
                                      size='sm'
                                      icon='ri-eye-line'
                                      onClick={() =>
                                        openDocument(
                                          procurement.documentNotaDinasUrl
                                        )
                                      }
                                    >
                                      View
                                    </ActionButton>
                                  )}
                                  <ActionButton
                                    variant='secondary'
                                    size='sm'
                                    icon='ri-upload-2-line'
                                    disabled={!!savingAction}
                                    onClick={() =>
                                      !savingAction &&
                                      fileInputRef.current?.click()
                                    }
                                  >
                                    Replace
                                  </ActionButton>
                                </div>
                              </div>
                            ) : (
                              <div
                                className={`border rounded-3 p-3 d-flex align-items-center bg-white ${
                                  errors.documentNotaDinas
                                    ? 'border-danger'
                                    : ''
                                }`}
                                style={{
                                  cursor: savingAction
                                    ? 'not-allowed'
                                    : 'pointer',
                                  opacity: savingAction ? 0.6 : 1,
                                }}
                                {...buttonProps(
                                  () =>
                                    !savingAction &&
                                    fileInputRef.current?.click(),
                                  'Upload file',
                                  { 'aria-disabled': Boolean(savingAction) }
                                )}
                              >
                                <div
                                  className='d-flex align-items-center justify-content-center rounded-circle me-3'
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor: errors.documentNotaDinas
                                      ? '#fdf2f2'
                                      : '#f3f6fa',
                                    flexShrink: 0,
                                  }}
                                >
                                  <i
                                    className={`ri-upload-2-line fs-20 ${errors.documentNotaDinas ? 'text-danger' : 'text-secondary'}`}
                                  ></i>
                                </div>
                                <div>
                                  <h5 className='fs-14 fw-semibold text-dark mb-0'>
                                    Upload File
                                  </h5>
                                  <span className='text-muted small fs-12'>
                                    Browse from your device
                                  </span>
                                </div>
                              </div>
                            )}

                            {errors.documentNotaDinas && (
                              <div className='text-danger small mt-2 fs-12 fw-medium d-flex align-items-center gap-1'>
                                <i className='ri-error-warning-line align-middle'></i>
                                {errors.documentNotaDinas}
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                      </>
                    )}
                  </Row>
                </Form>
              </div>
            </CardBody>
          </Card>

          <Card className='border shadow-sm'>
            <CardBody>
              <div className='mb-4'>
                <h5 className='fw-semibold mb-1'>Procurement Information</h5>
                <p className='text-muted small mb-0'>
                  Procurement details linked to this HPS request.
                </p>
              </div>

              <div className='border-bottom mb-4'></div>

              <Row className='g-4'>
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      Procurement Type
                    </Label>
                    <p className='mb-0 text-dark'>
                      {displayValue(procurement?.procurementTypeHeaderLabel)}
                    </p>
                  </div>
                </Col>
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      Title Procurement
                    </Label>
                    <p className='mb-0 text-dark'>
                      {displayValue(procurement?.titleProcurement)}
                    </p>
                  </div>
                </Col>
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      Proc Type
                    </Label>
                    <p className='mb-0 text-dark'>
                      {getProcurementTypeLabel(procurement?.procurementType)}
                    </p>
                  </div>
                </Col>
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      Contract Type
                    </Label>
                    <p className='mb-0 text-dark'>
                      {getContractTypeLabel(procurement?.contractType)}
                    </p>
                  </div>
                </Col>
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      {periodProps.startLabel}
                    </Label>
                    <p className='mb-0 text-dark'>{periodProps.startValue}</p>
                  </div>
                </Col>
                {periodProps.endLabel && (
                  <Col md={6} lg={4}>
                    <div className='border-start border-2 border-primary ps-3'>
                      <Label className='fw-semibold small text-dark mb-1'>
                        {periodProps.endLabel}
                      </Label>
                      <p className='mb-0 text-dark'>{periodProps.endValue}</p>
                    </div>
                  </Col>
                )}
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      Product
                    </Label>
                    <p className='mb-0 text-dark'>
                      {displayValue(procurement?.productType?.product?.name)}
                    </p>
                  </div>
                </Col>
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      Product Type
                    </Label>
                    <p className='mb-0 text-dark'>
                      {displayValue(
                        hasMultipleProductTypes(procurement)
                          ? formatProcurementProductTypeNames(procurement)
                          : procurement?.productType?.description
                      )}
                    </p>
                  </div>
                </Col>
                <Col md={6} lg={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <Label className='fw-semibold small text-dark mb-1'>
                      Submission Deadline (Jakarta Local Time)
                    </Label>
                    <p className='mb-0 text-dark'>
                      {formatProposalDeadline(
                        procurement?.proposalSubmissionDeadline
                      )}
                    </p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className='border shadow-sm'>
            <CardBody>
              <Alert
                color='info'
                className='d-flex align-items-start gap-2 border-0 bg-light text-primary'
              >
                <i className='ri-information-line flex-shrink-0 mt-1'></i>
                <span className='small'>
                  The detailed procurement plan information below can be
                  downloaded in Excel format to be used as an attachment in the
                  submission of the HPS Alpha Request Memorandum
                </span>
              </Alert>

              <div className='d-flex flex-wrap gap-2 justify-content-between align-items-start mb-4'>
                <div>
                  <h5 className='fw-semibold mb-1'>Parcel</h5>
                  <p className='text-muted small mb-0'>
                    Delivery terms and incoterm from procurement planning.
                  </p>
                </div>
                <ActionButton
                  variant='secondary'
                  size='sm'
                  icon='ri-file-excel-2-line'
                  disabled={isLoadingParcels}
                  loading={isExportingExcel}
                  onClick={exportParcelToExcel}
                >
                  Export to Excel
                </ActionButton>
              </div>

              <HpsParcelSection
                parcels={parcels}
                isLoadingParcels={isLoadingParcels}
                procurement={procurement}
                isReadOnly={isHpsSubmitted}
                onCargoRatioStatusChange={handleCargoRatioStatusChange}
                cargoRatioErrorParcelId={cargoRatioErrorParcelId}
                onClearCargoRatioError={handleClearCargoRatioError}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>

      {!isHpsSubmitted && (
        <div className='req-actionbar flex-wrap'>
          <Can action='canUpdate'>
            <ActionButton
              variant='secondary'
              icon='ri-draft-line'
              disabled={!!savingAction}
              loading={savingAction === 'draft'}
              onClick={handleSaveDraft}
            >
              Save as Draft
            </ActionButton>

            <ActionButton
              variant='primary'
              icon='ri-send-plane-line'
              disabled={!!savingAction}
              loading={savingAction === 'send'}
              onClick={handleSendRequestClick}
            >
              Send Request HPS
            </ActionButton>
          </Can>
        </div>
      )}

      <SubmitConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await handleSendRequest();
        }}
        title='Send HPS request?'
        message='The HPS request is sent right away and can no longer be edited. It cannot be undone.'
        confirmLabel='Send HPS request'
        isSubmitting={savingAction === 'send'}
      />
    </>
  );
};

export default RequestHpsMarket;
